<template>
  <div class="activity-create-page">
    <a-card :bordered="false">
      <template slot="title">
        <page-back-title :title="pageTitle" />
      </template>

      <a-spin :spinning="detailLoading">
        <div class="steps-wrap">
          <a-steps :current="current" @change="onStepChange">
            <a-step v-for="item in steps" :key="item" :title="item" />
          </a-steps>
        </div>

        <!-- 步骤1：活动基本信息 -->
        <div v-show="current === 0" class="step-panel step-panel--basic">
          <a-form-model
            ref="basicForm"
            :model="form"
            :rules="rules"
            :label-col="labelCol"
            :wrapper-col="wrapperCol"
          >
            <a-form-model-item label="标题" prop="activityTitle">
              <a-input v-model="form.activityTitle" placeholder="请输入" allow-clear />
            </a-form-model-item>
            <a-form-model-item label="活动起止日期" prop="dateRange">
              <a-range-picker
                v-model="form.dateRange"
                style="width: 100%"
                value-format="YYYY-MM-DD"
                format="YYYY-MM-DD"
                :disabled-date="disabledDate"
              />
            </a-form-model-item>
            <a-form-model-item label="活动规则" prop="activityRule">
              <a-textarea
                v-model="form.activityRule"
                placeholder="请输入"
                :rows="10"
                :max-length="500"
              />
            </a-form-model-item>
            <a-form-model-item :wrapper-col="offsetWrapperCol">
              <a-button type="primary" @click="onNext">下一步</a-button>
            </a-form-model-item>
          </a-form-model>
        </div>

        <!-- 步骤2：配置活动商品 -->
        <div v-show="current === 1" class="step-panel step-panel--product">
          <activity-product-config
            ref="productConfig"
            :detail-components="detailComponents"
            :activity-code="form.activityCode || ''"
            :background-color="form.backgroundColor || ''"
            @prev="onPrev"
            @next="onNext"
            @preview-save="onPreviewSave"
          />
        </div>

        <!-- 步骤3：确认保存 -->
        <div v-show="current === 2" class="step-panel step-panel--confirm">
          <activity-confirm-save
            :marketing-title.sync="form.marketingTitle"
            :cover-image.sync="form.coverImage"
            :publishing="publishing"
            :is-edit="!!form.activityCode"
            @prev="onPrev"
            @publish="onPublish"
          />
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'
import PageBackTitle from '@/views/service_new/coupon/components/PageBackTitle.vue'
import ActivityProductConfig from './ActivityProductConfig.vue'
import ActivityConfirmSave from './ActivityConfirmSave.vue'

export default {
  name: 'ActivityBasicInfo',
  components: {
    PageBackTitle,
    ActivityProductConfig,
    ActivityConfirmSave
  },
  data () {
    return {
      current: 0,
      steps: ['活动基本信息', '配置活动商品', '确认保存'],
      labelCol: { span: 4 },
      wrapperCol: { span: 12 },
      offsetWrapperCol: { span: 12, offset: 4 },
      detailLoading: false,
      publishing: false,
      // 传给步骤2回填；用 prop watch，避免 card loading 时 ref 丢失
      detailComponents: null,
      form: {
        activityCode: null,
        activityTitle: '',
        dateRange: [],
        activityRule: '',
        marketingTitle: '',
        coverImage: '',
        backgroundColor: ''
      },
      rules: {
        activityTitle: [{ required: true, message: '请输入标题', trigger: 'blur' }],
        dateRange: [{ required: true, message: '请选择活动起止日期', trigger: 'change' }],
        activityRule: [{ required: true, message: '请输入活动规则', trigger: 'blur' }]
      }
    }
  },
  computed: {
    pageTitle () {
      return this.form.activityCode ? '编辑活动' : '新建活动'
    }
  },
  mounted () {
    const activityCode = this.$route.query.activityCode
    if (activityCode) {
      this.loadDetail(activityCode)
    }
  },
  methods: {
    // 只能选择今天及之后的日期
    disabledDate (current) {
      return current && current < moment().startOf('day')
    },
    formatDate (value) {
      const time = moment(value)
      return time.isValid() ? time.format('YYYY-MM-DD') : ''
    },
    applyDetail (detail) {
      if (!detail) return
      this.form.activityCode = detail.activityCode || null
      this.form.activityTitle = detail.activityTitle || ''
      this.form.activityRule = detail.activityRule || ''
      this.form.marketingTitle = detail.marketingTitle || ''
      this.form.coverImage = detail.coverImage || ''
      this.form.backgroundColor = detail.backgroundColor || ''
      const startDate = this.formatDate(detail.startTime)
      const endDate = this.formatDate(detail.endTime)
      this.form.dateRange = startDate && endDate ? [startDate, endDate] : []
      // 通过 prop 驱动回填（组件重建后仍能应用）
      this.detailComponents = detail.components || []
    },
    loadDetail (activityCode) {
      this.detailLoading = true
      this.getHttpServices()
        .getNewServeService()
        .post(`/marketingActivity/detail/${activityCode}`)
        .then(res => {
          this.applyDetail(res || {})
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '获取活动详情失败')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    // 提交时再映射成接口字段
    buildSubmitParams () {
      const [startDate, endDate] = this.form.dateRange || []
      const productConfig = this.$refs.productConfig
      const components = productConfig && productConfig.getComponentsPayload
        ? productConfig.getComponentsPayload()
        : []
      const backgroundColor = productConfig && productConfig.getBackgroundColor
        ? productConfig.getBackgroundColor()
        : (this.form.backgroundColor || '')
      return {
        activityCode: this.form.activityCode || null,
        activityTitle: this.form.activityTitle,
        activityRule: this.form.activityRule,
        marketingTitle: this.form.marketingTitle,
        startTime: startDate ? `${startDate} 00:00:00` : '',
        endTime: endDate ? `${endDate} 23:59:59` : '',
        coverImage: this.form.coverImage,
        backgroundColor,
        components
      }
    },
    goToStep (target) {
      if (target === this.current) return
      if (target < 0 || target >= this.steps.length) return
      if (target < this.current) {
        this.current = target
        return
      }
      if (this.current === 0) {
        this.$refs.basicForm.validate(valid => {
          if (!valid) return
          this.current = target
        })
        return
      }
      this.current = target
    },
    onStepChange (current) {
      this.goToStep(current)
    },
    onPrev () {
      if (this.current > 0) this.goToStep(this.current - 1)
    },
    onNext () {
      if (this.current < this.steps.length - 1) this.goToStep(this.current + 1)
    },
    // 编辑时点效果预览：同步保存，不跳转
    onPreviewSave () {
      if (!this.form.activityCode) return
      const params = this.buildSubmitParams()
      this.getHttpServices()
        .getNewServeService()
        .post('/marketingActivity/save', params)
        .catch(err => {
          this.$message.error((err && err.msg) || '保存失败，请重试')
        })
    },
    onPublish () {
      if (this.publishing) return
      const params = this.buildSubmitParams()
      this.publishing = true
      this.getHttpServices()
        .getNewServeService()
        .post('/marketingActivity/save', params)
        .then(() => {
          this.$message.success(this.form.activityCode ? '保存成功' : '发布成功')
          this.$router.push({ name: 'AiMarketingService' })
        })
        .catch(err => {
          this.$message.error((err && err.msg) || '发布失败，请重试')
        })
        .finally(() => {
          this.publishing = false
        })
    }
  }
}
</script>

<style scoped lang="less">
.activity-create-page {
  .steps-wrap {
    max-width: 820px;
    margin: 8px auto 40px;
  }

  .step-panel--basic {
    max-width: 900px;
    margin: 0 auto;
  }

  .step-panel--product {
    width: 100%;
  }

  .step-panel--confirm {
    width: 100%;
  }
}
</style>
